import React from 'react';
import styled from 'styled-components';
import {FlexColumn, Heading, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {ReportDefinitionV1Tasks} from './contracts/report-definition.types';
import {BuilderMode} from './ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;

const Wrapper = styled(FlexColumn)`
	align-items: center;
	text-align: center;
	gap: ${tokens.spacing['200']};
	padding: ${tokens.spacing['800']} ${tokens.spacing['400']};
`;

/** Human label for a condition, derived from its dotted field path's last meaningful segment
 * (e.g. 'task.status.name' -> 'status'). A readable summary is enough to point the user at what
 * to widen; the conditions editor owns exact labels. */
function conditionLabel(field: string): string {
	const parts = field.split('.').filter(Boolean);
	// Skip the leading 'task' root and trailing 'name'/'info' leaves so 'task.status.name' reads
	// as 'status' rather than 'name'.
	const meaningful = parts.filter(p => p !== 'task' && p !== 'name' && p !== 'info');
	const chosen = meaningful[0] ?? parts[parts.length - 1] ?? field;
	return chosen.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

export function EmptyReportState({definition, mode}: {definition: ReportDefinitionV1Tasks; mode: BuilderMode}): JSX.Element {
	const {formatMessage} = useIntl();
	const conditions = definition.filter?.filterConditions ?? [];
	const labels = Array.from(new Set(conditions.map(c => conditionLabel(c.field)))).filter(Boolean);
	const explanation =
		labels.length > 0
			? formatMessage({id: 'ai_report_builder.empty.with_conditions'}, {conditions: labels.join(', ')})
			: formatMessage({id: 'ai_report_builder.empty.no_conditions'});
	const hint = formatMessage({
		id: mode === 'edit' ? 'ai_report_builder.empty.hint_edit' : 'ai_report_builder.empty.hint_view',
	});

	return (
		<Wrapper data-cy={'ai-report-builder-empty'}>
			<Heading size={'3'}>{formatMessage({id: 'ai_report_builder.empty.title'})}</Heading>
			<Text>{explanation}</Text>
			<Text>{hint}</Text>
		</Wrapper>
	);
}
